import { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

const STORAGE_KEY = "dyna_cookie_consent_v1";

type Consent = {
  essential: true;
  analytics: boolean;
  marketing: boolean;
  decidedAt: string;
};

const CookieConsent = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    try {
      const existing = localStorage.getItem(STORAGE_KEY);
      if (!existing) setOpen(true);
    } catch {
      setOpen(true);
    }
  }, []);

  const save = (c: Consent) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  const acceptAll = () =>
    save({ essential: true, analytics: true, marketing: true, decidedAt: new Date().toISOString() });
  const rejectNonEssential = () =>
    save({ essential: true, analytics: false, marketing: false, decidedAt: new Date().toISOString() });
  const saveCustom = () =>
    save({ essential: true, analytics, marketing, decidedAt: new Date().toISOString() });

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-auto md:max-w-md z-[60]"
        >
          <div className="bg-background border border-primary/50 rounded-2xl shadow-2xl shadow-primary/10 p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Cookie className="h-4 w-4 text-primary" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-heading text-sm mb-1">{t("cookies.title")}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {t("cookies.body")}
                </p>
              </div>
              <button
                onClick={rejectNonEssential}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label={t("cookies.close") as string}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {customize && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-3 mb-4"
              >
                <label className="flex items-start gap-3 text-xs cursor-not-allowed opacity-70">
                  <input type="checkbox" checked disabled className="mt-0.5 accent-primary" />
                  <span>
                    <span className="font-medium text-heading block">{t("cookies.essential")}</span>
                    <span className="text-muted-foreground">{t("cookies.essentialDesc")}</span>
                  </span>
                </label>
                <label className="flex items-start gap-3 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    className="mt-0.5 accent-primary"
                  />
                  <span>
                    <span className="font-medium text-heading block">{t("cookies.analytics")}</span>
                    <span className="text-muted-foreground">{t("cookies.analyticsDesc")}</span>
                  </span>
                </label>
                <label className="flex items-start gap-3 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={marketing}
                    onChange={(e) => setMarketing(e.target.checked)}
                    className="mt-0.5 accent-primary"
                  />
                  <span>
                    <span className="font-medium text-heading block">{t("cookies.marketing")}</span>
                    <span className="text-muted-foreground">{t("cookies.marketingDesc")}</span>
                  </span>
                </label>
              </motion.div>
            )}

            <div className="flex flex-wrap gap-2">
              {!customize ? (
                <>
                  <Button size="sm" className="rounded-lg flex-1" onClick={acceptAll}>
                    {t("cookies.acceptAll")}
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-lg flex-1 border-primary/60" onClick={() => setCustomize(true)}>
                    {t("cookies.customize")}
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-lg flex-1 text-muted-foreground hover:text-foreground" onClick={rejectNonEssential}>
                    {t("cookies.rejectNonEssential")}
                  </Button>
                </>
              ) : (
                <>
                  <Button size="sm" className="rounded-lg flex-1" onClick={saveCustom}>
                    {t("cookies.save")}
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-lg" onClick={() => setCustomize(false)}>
                    {t("cookies.back")}
                  </Button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
